/**
 * Infinite War rules façade — tick orchestration + public re-exports.
 * Domain logic lives in `sim/*`; do not grow this file with new subsystems.
 */
import { U } from "./sim/accessors";
import { decideAiAction } from "./sim/ai";
import { playCard } from "./sim/cards";
import { tickBlight, tickUnits } from "./sim/combat";
import { tickDemigods } from "./sim/demigod";
import { updateTileOwners } from "./sim/occupancy";
import { tickPendingBlasts, tickWaves } from "./sim/spells";
import { createInfiniteWarState as createRawState } from "./sim/state";
import { applyCrowdSeparation } from "./sim/swarm";
import type { InfiniteWarState } from "./sim/types";
import { tickSouls } from "./sim/veil";

export type {
  InfiniteWarState,
  IwAimMode,
  IwAiDecision,
  IwCardId,
  IwCardKind,
  IwCardState,
  IwDemigod,
  IwPendingBlast,
  IwRoleId,
  IwTeam,
  IwTileOwner,
  IwUnit,
  IwUnitPhase,
  IwWave,
} from "./sim/types";

export type { IwEvent, IwEventKindId } from "./infinite-war-events";
export { IwEventKind } from "./infinite-war-events";

export {
  aoeDamageMul,
  cardAim,
  cardAimHintKey,
  cardCost,
  cardDef,
  cardNeedsTarget,
  matchedRoleFor,
  roleStats,
  roleTier,
} from "./sim/accessors";
export { decideAiAction } from "./sim/ai";
export { applySummonDeploy, playCard, trySummon, trySummonPack } from "./sim/cards";
export { applyAoeHit } from "./sim/combat";
export {
  enemiesInPressHalf,
  tileContested,
  tileLivingPresence,
  tileOwner,
  updateTileOwners,
} from "./sim/occupancy";
export { castDelayedBlast, castShockwave, castZone } from "./sim/spells";
export {
  drainEvents,
  teamCount,
  totalSoulValue,
} from "./sim/state";
export { soulResidueCount, soulResidueCountFor } from "./sim/veil";
export { canAfford, spendableMana, spendMana } from "./sim/mana";

export function createInfiniteWarState(): InfiniteWarState {
  const state = createRawState();
  updateTileOwners(state);
  return state;
}

export function tickInfiniteWar(state: InfiniteWarState, dt: number): void {
  if (state.winner) return;
  const step = Math.min(Math.max(dt, 0), 0.05);
  state.time += step;
  state.manaCap = Math.min(
    U.mana.capMax,
    U.mana.capStart + Math.floor(state.time / U.mana.capStepSec),
  );
  for (const team of ["south", "north"] as const) {
    state.mana[team] = Math.min(state.manaCap, state.mana[team] + step * U.mana.perSecond);
  }

  state.aiThink -= step;
  if (state.aiThink <= 0) {
    state.aiThink = 1.25;
    const decision = decideAiAction(state);
    state.lastAiDecision = decision;
    if (decision.action === "play" && decision.card) {
      playCard(state, "north", decision.card, decision.targetX, decision.targetY);
    }
  }

  tickSouls(state, step);
  tickBlight(state, step);
  tickWaves(state, step);
  tickPendingBlasts(state, step);
  tickDemigods(state, step);
  tickUnits(state, step);
  applyCrowdSeparation(state, step);
  updateTileOwners(state);
}
